import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Database, 
  Users, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  HardDrive,
  Shield,
  Calendar
} from 'lucide-react';
import { SystemHealthDto } from '../../types';

interface SystemHealthPanelProps {
  systemHealth: SystemHealthDto;
  isLoading?: boolean;
}

const SystemHealthPanel: React.FC<SystemHealthPanelProps> = ({ systemHealth, isLoading }) => {
  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (uptime: string) => {
    // Assuming uptime is in format "DD.HH:MM:SS" or similar
    return uptime;
  };

  const getHealthStatus = (isHealthy: boolean) => {
    return isHealthy ? (
      <Badge variant="default" className="bg-green-500">
        <CheckCircle className="h-3 w-3 mr-1" />
        Healthy
      </Badge>
    ) : (
      <Badge variant="destructive">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Issues Detected
      </Badge>
    );
  };

  const healthMetrics = [
    {
      label: 'Database Response Time',
      value: `${systemHealth.databaseResponseTimeMs.toFixed(1)}ms`,
      icon: <Database className="h-4 w-4" />,
      status: systemHealth.databaseResponseTimeMs < 100 ? 'good' : 'warning',
      color: systemHealth.databaseResponseTimeMs < 100 ? 'text-green-600' : 'text-yellow-600'
    },
    {
      label: 'Active Sessions',
      value: systemHealth.activeSessions.toString(),
      icon: <Users className="h-4 w-4" />,
      status: 'good',
      color: 'text-accent-color'
    },
    {
      label: 'System Uptime',
      value: formatUptime(systemHealth.systemUptime),
      icon: <Clock className="h-4 w-4" />,
      status: 'good',
      color: 'text-green-600'
    },
    {
      label: 'Error Rate (24h)',
      value: `${systemHealth.errorRate24Hours.toFixed(2)}%`,
      icon: <AlertTriangle className="h-4 w-4" />,
      status: systemHealth.errorRate24Hours < 1 ? 'good' : 'warning',
      color: systemHealth.errorRate24Hours < 1 ? 'text-green-600' : 'text-yellow-600'
    },
    {
      label: 'Storage Usage',
      value: formatBytes(systemHealth.storageUsageBytes),
      icon: <HardDrive className="h-4 w-4" />,
      status: 'good',
      color: 'text-purple-600'
    },
    {
      label: 'Failed Logins (24h)',
      value: systemHealth.failedLoginAttempts24Hours.toString(),
      icon: <Shield className="h-4 w-4" />,
      status: systemHealth.failedLoginAttempts24Hours < 10 ? 'good' : 'warning',
      color: systemHealth.failedLoginAttempts24Hours < 10 ? 'text-green-600' : 'text-yellow-600'
    },
    {
      label: 'Last Backup',
      value: new Date(systemHealth.lastBackupDate).toLocaleDateString(),
      icon: <Calendar className="h-4 w-4" />,
      status: 'good',
      color: 'text-indigo-600'
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>System Health</span>
          </span>
          {getHealthStatus(systemHealth.isSystemHealthy)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {healthMetrics.map((metric, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={metric.color}>
                  {metric.icon}
                </div>
                <span className="text-sm font-medium">{metric.label}</span>
              </div>
              <span className={`text-sm font-semibold ${metric.color}`}>
                {metric.value}
              </span>
            </div>
          ))}
        </div>

        {systemHealth.healthWarnings.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="text-sm font-semibold text-yellow-800 mb-2">Health Warnings</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              {systemHealth.healthWarnings.map((warning, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthPanel;
